import React, { useState, useEffect } from 'react';
import { devLog } from '../lib/log';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { buildConsentPayload, markPendingConsent } from '../lib/legal';
import { buildUtmPayload } from '../lib/attribution';
import { PASSWORD_MIN_LENGTH, PASSWORD_POLICY_MESSAGE, PASSWORD_PLACEHOLDER, isPasswordValid } from '../lib/passwordPolicy';
import PasswordInput from './PasswordInput';
import './Register.css';

function Register() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
    role: 'athlete'
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [accepted, setAccepted] = useState(false);
  const navigate = useNavigate();
  const { signUp, signInWithGoogle, user, profile } = useAuth();

  // PB-002 — Registrazione con Google. Riusa la checkbox consensi `accepted`:
  // persiste il consenso prima del redirect e lo riscrive al ritorno.
  const handleGoogle = async () => {
    if (!accepted) {
      setError('Per continuare dichiara di aver letto Privacy e Termini.');
      return;
    }
    setError('');
    markPendingConsent();
    try {
      const { error: oauthError } = await signInWithGoogle();
      if (oauthError) setError(oauthError.message || 'Errore con l\'accesso Google.');
    } catch (e) {
      setError('Errore con l\'accesso Google.');
    }
  };

  // Redirect se già loggato
  useEffect(() => {
    if (user && profile) {
      navigate(profile.role === 'admin' ? '/admin' : '/athlete', { replace: true });
    }
  }, [user, profile, navigate]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    /*#__PURE__*/ devLog('🟢 [REGISTER] Starting registration flow...');
    /*#__PURE__*/ devLog('🟢 [REGISTER] Form data:', {
      name: formData.name,
      email: formData.email,
      role: formData.role,
      passwordLength: formData.password?.length
    });

    if (!formData.name || !formData.email || !formData.password) {
      setError('Compila tutti i campi');
      return;
    }

    if (!isPasswordValid(formData.password)) {
      setError(PASSWORD_POLICY_MESSAGE);
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      setError('Le password non corrispondono');
      return;
    }

    if (!accepted) {
      setError('Per registrarti devi dichiarare di aver letto l\'Informativa Privacy e accettare i Termini di utilizzo.');
      return;
    }

    setLoading(true);
    setError('');

    try {
      /*#__PURE__*/ devLog('🟢 [REGISTER] Calling signUp...');
      // Profilo unico: tutti nascono atleti; la Modalità Coach si attiva dopo dal profilo
      // Consenso + prima attribuzione UTM: entrambi finiscono nei metadata e
      // vengono persistiti nel profilo dal trigger handle_new_user().
      const result = await signUp(formData.email, formData.password, formData.name, 'athlete', {
        ...buildConsentPayload(),
        ...buildUtmPayload()
      });

      /*#__PURE__*/ devLog('🟢 [REGISTER] SignUp result:', result);
      /*#__PURE__*/ devLog('🟢 [REGISTER] Has user?', !!result?.user);
      /*#__PURE__*/ devLog('🟢 [REGISTER] Has session?', !!result?.session);
      /*#__PURE__*/ devLog('🟢 [REGISTER] User ID:', result?.user?.id);
      /*#__PURE__*/ devLog('🟢 [REGISTER] User metadata:', result?.user?.user_metadata);

      // Controlla se serve conferma email
      if (result?.user && !result?.session) {
        /*#__PURE__*/ devLog('⚠️ [REGISTER] No session - email confirmation required');
        setError('');
        alert('Registrazione completata! Controlla la tua email per confermare l\'account prima di accedere.');
        // Redirect al login dopo aver chiuso l'alert
        navigate('/');
        return;
      }

      /*#__PURE__*/ devLog('✅ [REGISTER] Registration successful, waiting for redirect...');
      // Se c'è una sessione, il redirect sarà gestito dall'useEffect sopra
    } catch (error) {
      console.error('❌ [REGISTER] Registration error:', error);
      console.error('❌ [REGISTER] Error message:', error.message);
      console.error('❌ [REGISTER] Error details:', JSON.stringify(error, null, 2));

      // Mostra l'errore REALE di Supabase
      let errorMessage = error.message || 'Errore durante la registrazione';

      // Solo traduci i messaggi più comuni, altrimenti mostra l'errore originale
      if (error.message?.includes('already registered')) {
        errorMessage = 'Questa email è già registrata';
      } else if (error.message?.includes('Password should be')) {
        errorMessage = PASSWORD_POLICY_MESSAGE;
      }

      setError(errorMessage);
    } finally {
      /*#__PURE__*/ devLog('🟢 [REGISTER] Setting loading to false');
      setLoading(false);
    }
  };

  return (
    <div className="register-container">
      <div className="register-card">
        <h1 className="register-title">Uptra</h1>
        <p className="register-subtitle">Crea il tuo account</p>

        <form className="register-form" onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="name">Nome completo</label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              placeholder="inserisci il tuo nome"
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="email">Email</label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              placeholder="inserisci la tua email"
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="password">Password</label>
            <PasswordInput
              id="password"
              name="password"
              value={formData.password}
              onChange={handleChange}
              placeholder={PASSWORD_PLACEHOLDER}
              autoComplete="new-password"
              minLength={PASSWORD_MIN_LENGTH}
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="confirmPassword">Conferma password</label>
            <PasswordInput
              id="confirmPassword"
              name="confirmPassword"
              value={formData.confirmPassword}
              onChange={handleChange}
              placeholder="ripeti la password"
              autoComplete="new-password"
              minLength={PASSWORD_MIN_LENGTH}
              required
            />
          </div>

          <p className="register-hint" style={{ fontSize: '12px', color: '#6b7280', margin: '4px 0 8px' }}>
            Sei un personal trainer? Dopo la registrazione puoi attivare la
            <strong> Modalità Coach</strong> dal tuo profilo.
          </p>

          <label style={{ display: 'flex', gap: '8px', alignItems: 'flex-start', fontSize: '13px', color: '#374151', margin: '4px 0 10px', lineHeight: 1.4 }}>
            <input
              type="checkbox"
              checked={accepted}
              onChange={(e) => setAccepted(e.target.checked)}
              style={{ marginTop: '2px', flexShrink: 0 }}
            />
            <span>
              Dichiaro di aver letto l'<Link to="/privacy" target="_blank" rel="noopener noreferrer">Informativa Privacy</Link> e
              accetto i <Link to="/termini" target="_blank" rel="noopener noreferrer">Termini di utilizzo</Link> di Uptra.
            </span>
          </label>

          {error && <div className="error-message">{error}</div>}

          <button type="submit" className="btn btn-register" disabled={loading || !accepted}>
            {loading ? 'Caricamento...' : 'Registrati'}
          </button>
        </form>

        {/* PB-002 — Registrazione con Google (usa la stessa checkbox consensi sopra) */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', margin: '16px 0 12px' }}>
          <div style={{ flex: 1, height: '1px', background: '#e5e7eb' }} />
          <span style={{ fontSize: '12px', color: '#9ca3af' }}>oppure</span>
          <div style={{ flex: 1, height: '1px', background: '#e5e7eb' }} />
        </div>
        <button
          type="button"
          onClick={handleGoogle}
          disabled={!accepted}
          style={{ width: '100%', padding: '12px', background: '#fff', color: '#374151', border: '1px solid #d1d5db', borderRadius: '12px', fontSize: '15px', fontWeight: 600, cursor: accepted ? 'pointer' : 'not-allowed', opacity: accepted ? 1 : 0.5, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px' }}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23z"/><path fill="#FBBC05" d="M5.84 14.1a6.6 6.6 0 0 1 0-4.2V7.06H2.18a11 11 0 0 0 0 9.88l3.66-2.84z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84C6.71 7.3 9.14 5.38 12 5.38z"/></svg>
          Continua con Google
        </button>

        <div className="register-footer">
          <p>
            Hai già un account? <Link to="/">Accedi</Link>
          </p>
        </div>
      </div>
    </div>
  );
}

export default Register;
